/** A page-owned transport. This bridge has no module state: bundles share the same sink. */
export type TruthRow = Record<string, unknown>;
export type LocalResourceValues = Partial<Record<'aps' | 'reaction' | 'weapon_attack' | 'head_attack' | 'movement_pct', number>>;
/** A room control declares its saved columns/families before the sink publishes them. Inputs remain held/read-only. */
export interface LocalMemberChange {
    id: string;
    ec?: TruthRow;
    ecRemove?: readonly string[];
    fields?: TruthRow;
    remove?: readonly string[];
    inventoryCharges?: readonly { id: string; charges: unknown }[];
}
export interface TruthSink {
    commit(command: TruthRow, verdict: TruthRow): Promise<TruthRow>;
    read(endpoint: string, command: TruthRow): Promise<TruthRow>;
    /** Save the inventory lane's browser decision in a room that owns its rows in memory. */
    moveInventory?(input: import('../janus-engine/inventory/memory-pickup').MemoryPickup): Promise<import('../janus-engine/inventory/memory-pickup').MemoryPickupReceipt[]>;
    entity(id: string): Promise<TruthRow | null>;
    /** A local sink's held saved rows for one encounter (read only); the live transport has none. */
    heldBase?(id: string): TruthRow | null;
    /** The combat caller has already projected this operation onto this exact held surface. */
    retainAppliedCombat?(operationId: string, surface: TruthRow): void;
}
export interface LocalTruthSink extends TruthSink {
    readonly surface: TruthRow;
    readonly logs: TruthRow[];
    /** Patch the local calendar checkpoint; clocks do not invalidate creature compiles. */
    patchClock(clock: import('./campaign-clock').CampaignClockCols): Promise<void>;
    /** Decide one actor's resource columns when its queued turn arrives, then paint only that row. */
    patchResources(actorId: string, update: (values: LocalResourceValues, actor: TruthRow) => void): Promise<void>;
    snapshot(): import('../janus-engine/compile/base-patch').BaseEnvelope;
    editMembers(prepare: (base: TruthRow, surface: TruthRow) => readonly LocalMemberChange[]): Promise<void>;
    /** `update` returns false when it changed nothing (no commit, no publish). */
    edit(update: (base: TruthRow, surface: TruthRow) => void | false): Promise<void>;
}

export function asTruthSink(value: unknown): TruthSink | null {
    return value !== null && typeof value === 'object' && !('nodeType' in value)
        && typeof (value as TruthSink).commit === 'function' ? value as TruthSink : null;
}

export function ftCurrentTruthSink(): TruthSink | null {
    const current = typeof window === 'undefined' ? null : Object.getOwnPropertyDescriptor(window, 'ftCurrentTruthSink')?.value;
    return typeof current === 'function' ? asTruthSink(current()) : null;
}

export class TruthSinkRefusal extends Error {
    constructor(readonly reason: string) {
        super(reason); this.name = 'TruthSinkRefusal';
        if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('ft:unported', { detail: { reason, message: trainingGroundsRefusalMessage(reason), mode: 'training-grounds' } }));
    }
}

/** Technical reason keys remain stable for diagnostics; players see the action and keep their practice room. */
export function trainingGroundsRefusalMessage(reason: string, action = 'This action'): string {
    const path = reason.match(/\/options\/[^\s?]+/)?.[0];
    if (path) {
        const name = path.split('/').pop()!.replace(/\.php$/, '');
        const names: Record<string, string> = { inventory_floor_move_truth_commit_inc: 'Moving floor items',
            inventory_trade_truth_commit_inc: 'Trading and training', inventory_flag_truth_commit_inc: 'Changing item visibility or wares',
            caster_inventory_truth_commit_inc: 'Adding inventory', spawn_rows_commit_inc: 'Spawning creatures or objects',
            geometry_relocation_truth_commit_inc: 'Moving between rooms', calendar_time_commit_inc: 'Changing the campaign calendar',
            destruction_chain_save_inc: 'Destroying items', generator_truth_commit_inc: 'Generating items',
            teleport_offer_inc: 'Teleport offers' };
        const words = name.replace(/^(?:ft3d_|gm_)/, '').replace(/(?:_truth|_commit|_inc|_save|_read)\b/g, '').replace(/_/g, ' ');
        action = names[name] ?? (words.charAt(0).toUpperCase() + words.slice(1));
    } else if (action === 'This action') {
        const capability = reason.match(/training-grounds:([a-z-]+?)(?:-unsupported|-not-held|-not-ported|:)/)?.[1];
        const names: Record<string, string> = { verdict: 'This ability effect', schema: 'This play action', read: 'Reading this play state',
            ability: 'This ability', movement: 'Movement', trap: 'Trap actions', minigame: 'Lock and trap actions',
            activity: 'This activity', charge: 'Changing charges', combat: 'This combat action', 'spell-prep': 'Preparing spells' };
        if (capability) action = names[capability] ?? (capability.charAt(0).toUpperCase() + capability.slice(1).replace(/-/g, ' '));
    }
    if (/stale|changed|reused/i.test(reason)) return `${action} could not be saved in the Training Grounds. Try again. Your practice room is still open.`;
    return `${action} isn't available in the Training Grounds yet. Your practice room is still open.`;
}

export function truthWriteRefusal(input: string): TruthSinkRefusal {
    return new TruthSinkRefusal(`training-grounds:write-refused:${new URL(input, 'https://truth.invalid/').pathname}`);
}
/** Errors cross the pre-template / app bundle boundary, where class identities differ. */
export function isTruthSinkRefusal(error: unknown): error is TruthSinkRefusal {
    return error !== null && typeof error === 'object' && (error as Error).name === 'TruthSinkRefusal'
        && typeof (error as TruthSinkRefusal).reason === 'string' && (error as TruthSinkRefusal).reason.startsWith('training-grounds:');
}

const LOCAL_WRITES = new Set(['ability_truth_commit_inc.php', 'attack_truth_commit_inc.php', 'turn_truth_commit_inc.php',
    'status_truth_commit_inc.php', 'resource_truth_commit_inc.php', 'action_truth_commit_inc.php', 'combat_truth_commit_inc.php',
    'time_truth_commit_inc.php', 'roll_truth_commit_inc.php', 'charge_truth_commit_inc.php', 'movement_truth_save_inc.php',
    'movement_solid_truth_inc.php',
    'ft3d_status_move_truth_commit_inc.php', 'sanguine_truth_commit_inc.php', 'activity_truth_commit_inc.php',
    'spell_prep_truth_commit_inc.php', 'trap_truth_commit_inc.php', 'minigame_truth_commit_inc.php',
    'inventory_equip_truth_commit_inc.php']);

export const LOCAL_TELEMETRY_PATHS: ReadonlySet<string> = new Set([
    '/options/game/actions/janus_refusal_event_inc.php', '/options/game/actions/ability_truth_event_inc.php',
    '/options/game/actions/janus_refusal_snapshot_inc.php',
]);

/** Audited SELECT-only catalog/form POSTs. Exact paths keep writes and unknown routes in the local sink. */
export const CATALOG_READ_POSTS: ReadonlySet<string> = new Set([
    '/options/api/creature_sheet_catalog_inc.php',
    '/options/api/object_inc.php',
    '/options/api/sanguine_extraordinary_spells_inc.php',
    '/options/api/spawn_inputs_inc.php',
    '/options/api/trap_mount_inputs_inc.php',
    '/options/search/search_add_inventory_inc.php',
    '/options/tables/open_table_item_inc.php',
    '/options/tables/table_item_inc.php',
    '/options/tools/inventory/forms/form_add_inventory_inc.php',
    '/options/tools/inventory/get_inventory_encounter_link_options.php',
    '/options/tools/inventory/get_inventory_key_link_options.php',
    '/options/tools/items/get_items_for_dynamic_select.php',
    '/options/tools/player_characters/forms/form_edit_player_backstory_inc.php',
    '/options/tools/player_characters/forms/form_edit_player_bonds_inc.php',
    '/options/tools/player_characters/forms/form_edit_player_description_inc.php',
    '/options/tools/player_characters/forms/form_edit_player_enemies_inc.php',
    '/options/tools/player_characters/forms/form_edit_player_experience_marks_inc.php',
    '/options/tools/player_characters/forms/form_edit_player_flaws_inc.php',
    '/options/tools/player_characters/forms/form_edit_player_ideals_inc.php',
    '/options/tools/player_characters/forms/form_edit_player_organizations_inc.php',
    '/options/tools/player_characters/forms/form_edit_player_other_notes_inc.php',
    '/options/tools/player_characters/forms/form_edit_player_traits_inc.php',
    '/options/tools/statuses/forms/form_add_status_pickers_inc.php',
    '/options/tools/statuses/forms/form_modify_creature_status_effects_inc.php',
    '/options/tools/statuses/forms/form_modify_inventory_status_effects_inc.php',
    '/options/tools/statuses/forms/form_modify_status_effect_inc.php',
    '/options/tools/statuses/refresh_creature_status_effects_list_inc.php',
    '/options/tools/statuses/refresh_inventory_status_effects_list_inc.php',
]);

/** The existing page fetch owner and saved-read worker both consult this predicate. */
export function isTruthRequest(input: string, method = 'GET'): boolean {
    const url = new URL(input, 'https://truth.invalid/');
    const verb = method.toUpperCase();
    if (!['GET', 'HEAD'].includes(verb)) return /\/options\//.test(url.pathname)
        && !(verb === 'POST' && CATALOG_READ_POSTS.has(url.pathname));
    if (/\/options\/api\/(?:object_inc|base_(?:entity|inventory)_catalog_inc)\.php$/.test(url.pathname)
        || /\/base_entity_inc\.php$/.test(url.pathname) && url.searchParams.get('entity') === 'definition') return false;
    return /\/options\/(?:game\/|tools\/(?:encounter|campaign)|charges\/|api\/(?:base_entity_inc|computed_cache_(?:get|put)_inc|.*(?:state|contests|relocation|perception|bindings).*_inc)\.php)/.test(url.pathname);
}

/** Keep the native call (including its arguments and response) untouched on ordinary pages. */
export function truthFetch(input: string, init?: RequestInit, live: typeof fetch = fetch): Promise<Response> {
    const sink = ftCurrentTruthSink();
    if (!sink || !isTruthRequest(input, init?.method)) return live(input, init);
    return (async () => {
        const url = new URL(input, 'https://truth.invalid/');
        const endpoint = url.pathname.split('/').pop()!;
        if (LOCAL_TELEMETRY_PATHS.has(url.pathname)) {
            // Snapshots are compressed binary. Reporting telemetry must never generate a second gameplay refusal.
            if (endpoint !== 'janus_refusal_snapshot_inc.php') {
                try { await sink.read('ability_truth_event_inc.php', { ...JSON.parse(String(init?.body ?? '{}')), context: 'training-grounds' }); }
                catch { /* Diagnostic delivery cannot fail an action. */ }
            }
            return Response.json({ ok: true });
        }
        const command: TruthRow = Object.fromEntries(url.searchParams);
        try {
            const method = (init?.method ?? 'GET').toUpperCase();
            const reads = ['counterspell_watch_inc.php', 'movement_truth_resync_inc.php', 'ability_truth_event_inc.php',
                'stealth_contests_inc.php', 'stealth_lockout_inc.php',
                'get_caster_arr.php', 'get_encounter_arr.php', 'base_entity_inc.php', 'push_turn_timer_inc.php',
                'activity_state_inc.php'];
            if (!['GET', 'HEAD'].includes(method) && !LOCAL_WRITES.has(endpoint) && !reads.includes(endpoint)) throw truthWriteRefusal(input);
            if (init?.body) {
                if (typeof init.body !== 'string') throw truthWriteRefusal(input);
                let body: unknown;
                try { body = JSON.parse(init.body); } catch { throw truthWriteRefusal(input); }
                if (!body || typeof body !== 'object' || Array.isArray(body)) throw truthWriteRefusal(input);
                Object.assign(command, body);
            }
            const read = method === 'GET' || method === 'HEAD' || endpoint.includes('read_')
                || endpoint === 'counterspell_watch_inc.php' || endpoint === 'movement_truth_resync_inc.php'
                || reads.includes(endpoint)
                || command.preflight === true || String(command.mode ?? '').startsWith('pending_');
            const verdict = (command.verdict ?? command.gm_boundary ?? command.frame ?? command.after ?? command.writes ?? command) as TruthRow;
            const result = read ? await sink.read(endpoint, command) : await sink.commit(command, verdict);
            return Response.json(result, { status: result.ok === false ? 409 : 200 });
        } catch (error) {
            if (!isTruthSinkRefusal(error)) throw error;
            return Response.json({ ok: false, error: error.reason, reason: error.reason }, { status: 409 });
        }
    })();
}

declare global {
    interface Window {
        ftCreateMemoryTruthSink?: (envelope: import('../janus-engine/compile/base-patch').BaseEnvelope,
            project?: (computed: TruthRow, base: TruthRow) => void) => LocalTruthSink;
        ftMountLocalEncounter?: (mount: HTMLElement, encounter: TruthRow, shape: TruthRow) => Promise<{ update(encounter: TruthRow): Promise<void> }>;
        ftInstallTruthSink?: (sink: TruthSink | null) => void;
        ftCurrentTruthSink?: typeof ftCurrentTruthSink;
    }
}
